/**
 * Capitalizes the first letter of each word in a given string.
 *
 * @param str the string to be capitalized.
 * @returns {string} a string with the first letter of each word capitalized.
 */
export const capitalizeFirstLetters = (str: string | null | undefined): string => {
  if (!str) return ''
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Trims and collapses repeated internal whitespace, returning null when nothing is left.
 */
export function normalizeWhitespace(value: string | null | undefined): string | null {
  const normalized = value?.replace(/\s+/g, ' ').trim()
  return normalized ? normalized : null
}

/**
 * Joins first and last name parts into a single display string, tolerating missing parts.
 * Each part is whitespace-normalized; empty or whitespace-only parts are treated as missing.
 * Returns null when neither part has content.
 */
export function joinNameParts(firstName: string | null | undefined, lastName: string | null | undefined): string | null {
  const first = normalizeWhitespace(firstName)
  const last = normalizeWhitespace(lastName)
  if (first && last) return `${first} ${last}`
  return first ?? last ?? null
}

/**
 * Extracts 1-2 character initials from a name or email string.
 * - Single word: first character (e.g., "alice" → "A")
 * - Multiple words: first + last initials (e.g., "alice smith" → "AS")
 */
export function getInitialsFromString(raw: string): string {
  const words = raw.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return words[0].charAt(0).toUpperCase() + words[words.length - 1].charAt(0).toUpperCase()
}
